import {
    areSearchParamsEqual,
    ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type MonitoringFilterItem = { key: string; value: string };

export type MonitoringUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    filterCampaignId: MonitoringFilterItem | null;
    filterNameTo: MonitoringFilterItem | null;
    filterANumber: MonitoringFilterItem | null;
    filterNameFrom: MonitoringFilterItem | null;
    filterTypeChannel: MonitoringFilterItem[] | null;
    filterOwnerType: MonitoringFilterItem[] | null;
    filterFullName: MonitoringFilterItem[] | null;
    filterCampaignName: MonitoringFilterItem[] | null;
    filterBranchName: MonitoringFilterItem[] | null;
    filterCallOwner: MonitoringFilterItem[] | null;
    filterQueueName: MonitoringFilterItem[] | null;
    filterDirection: MonitoringFilterItem[] | null;
    filterStatus: MonitoringFilterItem[] | null;
    filterAmdHangup: MonitoringFilterItem[] | null;
    filterCallReset: MonitoringFilterItem[] | null;
    filterDateTime: Date | null;
    filterEndDateTime: Date | null;
    startTime: Date | null;
    endTime: Date | null;
    hasStartFilter: boolean;
    filterFinishDateTime: Date | null;
    filterFinishEndDateTime: Date | null;
    startFinishTime: Date | null;
    endFinishTime: Date | null;
    hasEndFilter: boolean;
    duration: boolean;
    durationStartTime: Date | null;
    durationEndTime: Date | null;
    fullCallDuration: boolean;
    fullCallDurationStartTime: Date | null;
    fullCallDurationEndTime: Date | null;
    reactionClient: boolean;
    reactionClientStartTime: Date | null;
    reactionClientEndTime: Date | null;
    reactionOperator: boolean;
    reactionOperatorStartTime: Date | null;
    reactionOperatorEndTime: Date | null;
    uiFilters: Record<string, any>;
};

export type MonitoringUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    filterCampaignId: MonitoringFilterItem | null;
    filterNameTo: MonitoringFilterItem | null;
    filterANumber: MonitoringFilterItem | null;
    filterNameFrom: MonitoringFilterItem | null;
    filterTypeChannel: MonitoringFilterItem[] | null;
    filterOwnerType: MonitoringFilterItem[] | null;
    filterFullName: MonitoringFilterItem[] | null;
    filterCampaignName: MonitoringFilterItem[] | null;
    filterBranchName: MonitoringFilterItem[] | null;
    filterCallOwner: MonitoringFilterItem[] | null;
    filterQueueName: MonitoringFilterItem[] | null;
    filterDirection: MonitoringFilterItem[] | null;
    filterStatus: MonitoringFilterItem[] | null;
    filterAmdHangup: MonitoringFilterItem[] | null;
    filterCallReset: MonitoringFilterItem[] | null;
    filterDateTime: any;
    filterEndDateTime: any;
    startTime: any;
    endTime: any;
    hasStartFilter: boolean;
    filterFinishDateTime: any;
    filterFinishEndDateTime: any;
    startFinishTime: any;
    endFinishTime: any;
    hasEndFilter: boolean;
    writeCustomStartRangeOnly?: boolean;
    duration: boolean;
    durationStartTime: any;
    durationEndTime: any;
    fullCallDuration: boolean;
    fullCallDurationStartTime: any;
    fullCallDurationEndTime: any;
    reactionClient: boolean;
    reactionClientStartTime: any;
    reactionClientEndTime: any;
    reactionOperator: boolean;
    reactionOperatorStartTime: any;
    reactionOperatorEndTime: any;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;
const NUMERIC_MULTI_KEYS = new Set([
    'status',
    'callReset',
    'forcedClosing',
    'ownerType',
    'campaign',
    'branchId',
    'queueId',
]);

export function getMonitoringSearchParamsFromHash(): URLSearchParams {
    return getHashSearchParams();
}

export function readInitialMonitoringUrlState(): MonitoringUrlViewState {
    return parseMonitoringUrlState(getMonitoringSearchParamsFromHash());
}

export function parseMonitoringUrlState(
    params: URLSearchParams,
    encodedKey: string = ENCODED_QUERY_KEY
): MonitoringUrlViewState {
    params = unwrapEncodedSearchParams(params, encodedKey);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);

    const ani = params.get('ani');
    const aNumber = params.get('aNumber');
    const userName = params.get('userName');
    const campaignId = params.get('campaignId');

    const fullName = getMultiParams(params, 'fullName');
    const ownerUserName = getMultiParams(params, 'ownerUserName');
    const ownerType = getMultiParams(params, 'ownerType', true);
    const campaign = getMultiParams(params, 'campaign', true);
    const branchId = getMultiParams(params, 'branchId', true);
    const queueId = getMultiParams(params, 'queueId', true);
    const status = getMultiParams(params, 'status', true);
    const callReset = getMultiParams(params, 'callReset', true);
    const forcedClosing = getMultiParams(params, 'forcedClosing', true);
    const callType = getMultiParams(params, 'callType');
    const direction = getMultiParams(params, 'direction');

    const startRange = parseDateTimeRange(params.get('startFrom'), params.get('startTo'));
    const endRange = parseDateTimeRange(params.get('endFrom'), params.get('endTo'));
    const durationRange = parseSecondsRange(params.get('durationFrom'), params.get('durationTo'));
    const fullCallDurationRange = parseSecondsRange(params.get('fullCallDurationFrom'), params.get('fullCallDurationTo'));
    const reactionClientRange = parseSecondsRange(params.get('reactionClientFrom'), params.get('reactionClientTo'));
    const reactionOperatorRange = parseSecondsRange(params.get('reactionOperatorFrom'), params.get('reactionOperatorTo'));

    if (ani) uiFilters.ani = ani;
    if (aNumber) uiFilters.aNumber = aNumber;
    if (userName) uiFilters.userName = userName;
    if (campaignId) uiFilters.campaignId = campaignId;
    if (fullName.length) uiFilters.fullName = fullName;
    if (ownerUserName.length) uiFilters.ownerUserName = ownerUserName;
    if (ownerType.length) uiFilters.ownerType = ownerType;
    if (campaign.length) uiFilters.campaignFullName = campaign;
    if (branchId.length) uiFilters.branchId = branchId;
    if (queueId.length) uiFilters.queueFullName = queueId;
    if (status.length) uiFilters.status = status;
    if (callReset.length) uiFilters.callResetName = callReset;
    if (forcedClosing.length) uiFilters.forcedClosing = forcedClosing;
    if (callType.length) uiFilters.callType = callType;
    if (direction.length) uiFilters.direction = direction;
    if (startRange) uiFilters.start = startRange.startDate;
    if (endRange) uiFilters.end = endRange.startDate;
    if (durationRange) uiFilters.duration = durationRange.start;
    if (fullCallDurationRange) uiFilters.fullCallDuration = fullCallDurationRange.start;
    if (reactionClientRange) uiFilters.reactionClient = reactionClientRange.start;
    if (reactionOperatorRange) uiFilters.reactionOperator = reactionOperatorRange.start;

    const hasAny = params.toString().length > 0;

    return {
        hasAny,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        filterCampaignId: toFilterItem('CampaignId', campaignId),
        filterNameTo: toFilterItem('ANI', ani),
        filterANumber: toFilterItem('ANumber', aNumber),
        filterNameFrom: toFilterItem('UserName', userName),
        filterTypeChannel: toFilterList('CallType', callType),
        filterOwnerType: toFilterList('OwnerType', ownerType),
        filterFullName: toFilterList('UserName', fullName),
        filterCampaignName: toFilterList('CampaignId', campaign),
        filterBranchName: toFilterList('BranchId', branchId),
        filterCallOwner: toFilterList('OwnerUserName', ownerUserName),
        filterQueueName: toFilterList('QueueId', queueId),
        filterDirection: toFilterList('Direction', direction),
        filterStatus: toFilterList('Status', status),
        filterAmdHangup: toFilterList('ForcedClosing', forcedClosing),
        filterCallReset: toFilterList('CallReset', callReset),
        filterDateTime: startRange?.startDate ?? null,
        filterEndDateTime: startRange?.endDate ?? null,
        startTime: startRange?.startTime ?? null,
        endTime: startRange?.endTime ?? null,
        hasStartFilter: !!startRange,
        filterFinishDateTime: endRange?.startDate ?? null,
        filterFinishEndDateTime: endRange?.endDate ?? null,
        startFinishTime: endRange?.startTime ?? null,
        endFinishTime: endRange?.endTime ?? null,
        hasEndFilter: !!endRange,
        duration: !!durationRange,
        durationStartTime: durationRange?.start ?? null,
        durationEndTime: durationRange?.end ?? null,
        fullCallDuration: !!fullCallDurationRange,
        fullCallDurationStartTime: fullCallDurationRange?.start ?? null,
        fullCallDurationEndTime: fullCallDurationRange?.end ?? null,
        reactionClient: !!reactionClientRange,
        reactionClientStartTime: reactionClientRange?.start ?? null,
        reactionClientEndTime: reactionClientRange?.end ?? null,
        reactionOperator: !!reactionOperatorRange,
        reactionOperatorStartTime: reactionOperatorRange?.start ?? null,
        reactionOperatorEndTime: reactionOperatorRange?.end ?? null,
        uiFilters,
    };
}

export function buildMonitoringSearchParams(
    state: MonitoringUrlWriteState,
    encodedKey: string = ENCODED_QUERY_KEY
): URLSearchParams {
    return wrapEncodedSearchParams(buildPlainMonitoringSearchParams(state), encodedKey);
}

function buildPlainMonitoringSearchParams(state: MonitoringUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'Id' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }

    setScalar(params, 'ani', state.filterNameTo?.value);
    setScalar(params, 'aNumber', state.filterANumber?.value);
    setScalar(params, 'userName', state.filterNameFrom?.value);
    setScalar(params, 'campaignId', state.filterCampaignId?.value);

    setMulti(params, 'fullName', state.filterFullName);
    setMulti(params, 'ownerUserName', state.filterCallOwner);
    setMulti(params, 'ownerType', state.filterOwnerType);
    setMulti(params, 'campaign', state.filterCampaignName);
    setMulti(params, 'branchId', state.filterBranchName);
    setMulti(params, 'queueId', state.filterQueueName);
    setMulti(params, 'status', state.filterStatus);
    setMulti(params, 'callReset', state.filterCallReset);
    setMulti(params, 'forcedClosing', state.filterAmdHangup);
    setMulti(params, 'callType', state.filterTypeChannel);
    setMulti(params, 'direction', state.filterDirection);

    if (shouldWriteStartRange(state)) {
        const startFrom = combineDateAndTime(state.filterDateTime, state.startTime, true);
        const startTo = combineDateAndTime(state.filterEndDateTime, state.endTime, false);
        if (startFrom) params.set('startFrom', toLocalIso(startFrom));
        if (startTo) params.set('startTo', toLocalIso(startTo));
    }

    if (state.hasEndFilter) {
        const endFrom = combineDateAndTime(state.filterFinishDateTime, state.startFinishTime, true);
        const endTo = combineDateAndTime(state.filterFinishEndDateTime, state.endFinishTime, false);
        if (endFrom) params.set('endFrom', toLocalIso(endFrom));
        if (endTo) params.set('endTo', toLocalIso(endTo));
    }

    setSecondsRange(params, 'durationFrom', 'durationTo', state.duration, state.durationStartTime, state.durationEndTime);
    setSecondsRange(params, 'fullCallDurationFrom', 'fullCallDurationTo', state.fullCallDuration, state.fullCallDurationStartTime, state.fullCallDurationEndTime);
    setSecondsRange(params, 'reactionClientFrom', 'reactionClientTo', state.reactionClient, state.reactionClientStartTime, state.reactionClientEndTime);
    setSecondsRange(params, 'reactionOperatorFrom', 'reactionOperatorTo', state.reactionOperator, state.reactionOperatorStartTime, state.reactionOperatorEndTime);

    return params;
}

function shouldWriteStartRange(state: MonitoringUrlWriteState): boolean {
    const startFrom = combineDateAndTime(state.filterDateTime, state.startTime, true);
    const startTo = combineDateAndTime(state.filterEndDateTime, state.endTime, false);
    if (!startFrom || !startTo) return false;
    if (state.writeCustomStartRangeOnly) {
        return !isDefaultDateRange(startFrom, startTo);
    }
    return !!state.hasStartFilter;
}

function isDefaultDateRange(startFrom: Date, startTo: Date): boolean {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 0);
    return toLocalIso(startFrom) === toLocalIso(todayStart) && toLocalIso(startTo) === toLocalIso(todayEnd);
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function setMulti(params: URLSearchParams, key: string, items?: MonitoringFilterItem[] | null) {
    items?.forEach((item) => {
        if (item?.value) {
            params.append(key, item.value);
        }
    });
}

function setSecondsRange(
    params: URLSearchParams,
    fromKey: string,
    toKey: string,
    enabled: boolean,
    startTime: any,
    endTime: any
) {
    if (!enabled) return;
    const from = getTotalSeconds(startTime);
    const to = getTotalSeconds(endTime);
    if (from == null || to == null) return;
    if (from === 0 && to === 0) return;
    params.set(fromKey, String(from));
    params.set(toKey, String(to));
}

function getMultiParams(params: URLSearchParams, key: string, numeric = false): Array<string | number> {
    const values = params.getAll(key)
        .flatMap((value) => value.split(','))
        .map((value) => value.trim())
        .filter(Boolean);

    if (!numeric && !NUMERIC_MULTI_KEYS.has(key)) {
        return values;
    }

    return values.map((value) => coerceNumeric(value));
}

function coerceNumeric(value: string): string | number {
    if (/^-?\d+$/.test(value)) {
        const parsed = Number(value);
        if (Number.isSafeInteger(parsed)) {
            return parsed;
        }
    }
    return value;
}

function toFilterItem(key: string, value?: string | null): MonitoringFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toFilterList(key: string, values?: Array<string | number>): MonitoringFilterItem[] | null {
    if (!values?.length) return null;
    return values.map((value) => ({ key, value: String(value) }));
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}

function toLocalIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function fromLocalIso(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6])
    );
}

function parseDateTimeRange(fromValue: string | null, toValue: string | null) {
    if (!fromValue || !toValue) return null;
    const from = fromLocalIso(fromValue);
    const to = fromLocalIso(toValue);
    if (!from || !to) return null;

    return {
        startDate: startOfDay(from),
        endDate: startOfDay(to),
        startTime: timeOnly(from),
        endTime: timeOnly(to),
    };
}

function parseSecondsRange(fromValue: string | null, toValue: string | null) {
    if (fromValue == null || toValue == null) return null;
    const from = Number(fromValue);
    const to = Number(toValue);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
    if (from === 0 && to === 0) return null;
    return {
        start: secondsToDate(from),
        end: secondsToDate(to),
    };
}

function startOfDay(date: Date): Date {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
}

function timeOnly(date: Date): Date {
    const next = new Date();
    next.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);
    return next;
}

function secondsToDate(totalSeconds: number): Date {
    const next = new Date();
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    next.setHours(hours, minutes, seconds, 0);
    return next;
}

function getTotalSeconds(date: any): number | null {
    if (!date || typeof date.getHours !== 'function') return null;
    return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
}

function combineDateAndTime(dateValue: any, timeValue: any, isStart: boolean): Date | null {
    const date = toValidDate(dateValue) || new Date();
    const time = toValidDate(timeValue);
    const result = new Date(date);
    if (time) {
        result.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
    } else {
        result.setHours(isStart ? 0 : 23, isStart ? 0 : 59, isStart ? 0 : 59, 0);
    }
    return result;
}

function toValidDate(value: any): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
